import type { IntlShape } from 'react-intl';

import type { ExtensionManifest, ToolbarItem } from '@atlaskit/editor-common/extensions';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import {
	AGENT_MANAGED_EXTENSION_KEY,
	AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	type AgentManagedExtensionAttributes,
	type AgentManagedExtensionPlugin,
} from '../common/agentManagedExtensionPluginType';
import { messages } from '../common/messages';
import { getNodeAiManagedObject } from '../common/utils';
import { getAutoUpdateIntervalToolbarItem } from '../plugin/ui/AutoUpdateIntervalSelector';

type ContextualToolbar = NonNullable<ExtensionManifest['modules']['contextualToolbars']>[number];
type ContextualToolbarNode = { attrs?: unknown };

const customizeIcon = () =>
	import(
		/* webpackChunkName: "@atlaskit-internal_icon-customize" */ '@atlaskit/icon/core/customize'
	).then((mod) => mod.default);

const getAgentManagedExtensionAttributes = (
	node: ContextualToolbarNode,
): AgentManagedExtensionAttributes => (node.attrs ?? {}) as AgentManagedExtensionAttributes;

const hasAiManagedObject = (
	node: ContextualToolbarNode,
	editorAPI?: PublicPluginAPI<[AgentManagedExtensionPlugin]>,
): boolean => {
	const attrs = getAgentManagedExtensionAttributes(node);
	return (
		Boolean(getNodeAiManagedObject(attrs)) ||
		Boolean(editorAPI?.agentManagedExtension?.actions?.hasAiManagedObject(attrs.localId))
	);
};

/**
 * Manifest-contributed contextual toolbar items for the agent-managed
 * bodied extension. We contribute Customize for all matching blocks,
 * plus Auto-update interval once the block has an associated AIMO. The
 * framework's default extension toolbar provides the rest (layout
 * dropdown, edit, copy, delete).
 *
 * Customize bridges to the plugin by calling the `toggleConfig` action,
 * which dispatches a `TOGGLE_CONFIG` meta transaction via
 * `api.core.actions.execute`.
 */
export const getContextualToolbars = ({
	editorAPI,
	extensionType = AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	intl,
}: {
	editorAPI?: PublicPluginAPI<[AgentManagedExtensionPlugin]>;
	extensionType?: string;
	intl: IntlShape;
}): NonNullable<ExtensionManifest['modules']['contextualToolbars']> => {
	const autoUpdateIntervalItem = getAutoUpdateIntervalToolbarItem(intl, editorAPI);

	const customizeItem: ToolbarItem = {
		key: 'agent-managed-extension-customize',
		label: intl.formatMessage(messages.customize),
		tooltip: intl.formatMessage(messages.customize),
		display: 'icon-and-label',
		icon: customizeIcon,
		action: () => {
			editorAPI?.agentManagedExtension?.actions?.toggleConfig();
			return Promise.resolve();
		},
	};

	const toolbar: ContextualToolbar = {
		context: {
			type: 'extension',
			nodeType: 'bodiedExtension',
			extensionType,
			extensionKey: AGENT_MANAGED_EXTENSION_KEY,
		},
		toolbarItems: (node) =>
			hasAiManagedObject(node, editorAPI)
				? [customizeItem, autoUpdateIntervalItem]
				: [customizeItem],
	};

	return [toolbar];
};
